export type AspectRatio = '1:1' | '4:5' | '16:9';

export interface ApiErrorBody {
  error?: string;
  code?: string;
  message?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(response.status, 'Invalid JSON response');
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init?.headers,
    },
    ...init,
  });

  if (!response.ok) {
    const body = await parseJson<ApiErrorBody>(response).catch(() => ({} as ApiErrorBody));
    throw new ApiError(
      response.status,
      body.message ?? body.error ?? response.statusText,
      body.error ?? body.code,
    );
  }

  if (response.status === 204) return undefined as T;
  return parseJson<T>(response);
}

export interface ProfileSummary {
  id: string;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
}

export interface ChannelSummary {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  owner?: ProfileSummary | null;
}

export interface FeedItem {
  id: string;
  title: string;
  posterUrl?: string | null;
  aspectRatio?: AspectRatio;
  priceCents?: number | null;
  creator: ProfileSummary;
  channel?: Pick<ChannelSummary, 'slug' | 'name'>;
}

export interface FeedResponse {
  items: FeedItem[];
}

export interface FeaturedChannel extends ChannelSummary {
  assetIds?: string[];
  assets?: FeedItem[];
  previewAssets?: FeedItem[];
  channel?: ChannelSummary;
}

export interface FeaturedResponse {
  channels: Array<{
    id: string;
    channel: ChannelSummary & { creator?: ProfileSummary | null };
    assets: FeedItem[];
  }>;
}

export interface SessionResponse {
  authenticated: boolean;
  keyraVerified?: boolean;
  profile?: ProfileSummary | null;
  user?: ProfileSummary | null;
}

export interface PreviewGrantResponse {
  granted: boolean;
  reopen?: boolean;
  grantCount?: number;
  assetId?: string;
}

export type AssetStatus = 'private' | 'published';
export type AssetOrigin = 'uploaded' | 'generated' | 'acquired';

export interface LibraryItem {
  id: string;
  title: string;
  posterUrl?: string | null;
  status: AssetStatus;
  origin: AssetOrigin;
  priceCents?: number | null;
  updatedAt: string;
}

export interface LibraryResponse {
  owned: LibraryItem[];
  acquired: LibraryItem[];
  favorites?: LibraryItem[];
  counts?: { owned: number; acquired: number; favorites: number };
}

export type GenerationJobState =
  | 'DRAFT'
  | 'VALIDATING'
  | 'QUEUED'
  | 'PLANNING'
  | 'BUILDING'
  | 'RENDERING'
  | 'CHECKING'
  | 'REVISING'
  | 'PACKAGING'
  | 'READY'
  | 'FAILED'
  | 'CANCEL_REQUESTED'
  | 'CANCELED';

export interface GenerationJob {
  id: string;
  state: GenerationJobState;
  briefId: string;
  resultAssetId?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface AiConnection {
  id: string;
  name: string;
  modelId: string;
  health: string;
  paysForUsage: boolean;
  credentialRef?: string;
}

export const api = {
  health: () => request<{ ok: boolean }>('/api/v1/health'),

  getFeed: () => request<FeedResponse>('/api/v1/feed'),

  getFeatured: async () => {
    const data = await request<FeaturedResponse>('/api/v1/featured-channels');
    return {
      channels: data.channels.map((row) => ({
        id: row.channel.id,
        slug: row.channel.slug,
        name: row.channel.name,
        description: row.channel.description,
        owner: row.channel.creator ?? row.channel.owner ?? null,
        assets: row.assets.map((a) => ({
          ...a,
          creator: a.creator ?? row.channel.creator ?? { id: '', username: row.channel.slug },
        })),
      })) as FeaturedChannel[],
    };
  },

  getSession: () => request<SessionResponse>('/api/v1/auth/session'),

  requestPreviewGrant: (assetId: string) =>
    request<PreviewGrantResponse>('/api/v1/guest/preview-grant', {
      method: 'POST',
      body: JSON.stringify({ assetId }),
    }),

  getAsset: (assetId: string) =>
    request<{ asset: FeedItem & { access?: { canViewRuntime?: boolean; hasGuestGrant?: boolean } } }>(
      `/api/v1/assets/${assetId}`,
    ),

  getLibrary: () => request<LibraryResponse>('/api/v1/library'),

  getStudioCapabilities: () =>
    request<{
      capabilities: Array<{
        id: string;
        operation: string;
        availability: string;
        availabilityReason?: string;
        provider?: string;
      }>;
    }>('/api/v1/studio/capabilities'),

  quoteStudioJob: (body: { capabilityId: string; parameters?: Record<string, unknown> }) =>
    request<{
      quoteId: string;
      estimatedCostCents: number;
      currency: string;
      expiresAt: string;
      billable: boolean;
      note: string;
    }>('/api/v1/studio/jobs/quote', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createStudioJob: (body: {
    capabilityId: string;
    quoteId: string;
    idempotencyKey: string;
    prompt?: string;
    title?: string;
    parameters?: Record<string, unknown>;
    sourceUploadIds?: string[];
  }) =>
    request<{
      submission: { id: string; state: string; generationJobId?: string | null };
      job?: GenerationJob;
      idempotent: boolean;
    }>('/api/v1/studio/jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getStudioJob: (id: string) =>
    request<{
      submission: { id: string; state: string };
      job: GenerationJob | null;
    }>(`/api/v1/studio/jobs/${id}`),

  cancelStudioJob: (id: string) =>
    request<{ canceled: boolean; reason: string; state: string }>(
      `/api/v1/studio/jobs/${id}/cancel-request`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  agentBrief: (userRequest: string, budgetCents?: number) =>
    request<{
      brief: {
        proposedPrompt: string;
        suggestions: string[];
        proposedSequence: Array<{ step: number; action: string; costCents: number }>;
        disclaimer: string;
      };
    }>('/api/v1/studio/agent/brief', {
      method: 'POST',
      body: JSON.stringify({ request: userRequest, budgetCents }),
    }),

  getConnectors: () =>
    request<{
      connectors: Array<{
        id: string;
        name: string;
        category: string;
        status: string;
        operation: string;
        prerequisites: string;
        lastSuccessfulTest: string | null;
        supportRoute: string;
        formats: string[];
      }>;
    }>('/api/v1/connectors'),

  addFavorite: (assetId: string) =>
    request<{ favorited: boolean }>(`/api/v1/assets/${assetId}/favorite`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  removeFavorite: (assetId: string) =>
    request<{ favorited: boolean }>(`/api/v1/assets/${assetId}/favorite`, {
      method: 'DELETE',
    }),

  getFavoriteStatus: (assetId: string) =>
    request<{ favorited: boolean }>(`/api/v1/assets/${assetId}/favorite`),

  savePresentation: (
    assetId: string,
    body: { label?: string; settings: Record<string, unknown>; makeCurrent?: boolean },
  ) =>
    request<{ presentation: { id: string; settings: Record<string, unknown> } }>(
      `/api/v1/assets/${assetId}/presentations`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  getCurrentPresentation: (assetId: string) =>
    request<{ presentation: { id: string; settings: Record<string, unknown> } | null }>(
      `/api/v1/assets/${assetId}/presentations/current`,
    ),

  createJob: (payload: { prompt: string; aiConnectionId?: string | null; title?: string }) =>
    request<{ job: GenerationJob }>('/api/v1/create/jobs', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getJob: (jobId: string) => request<{ job: GenerationJob }>(`/api/v1/create/jobs/${jobId}`),

  enhancePrompt: (prompt: string) =>
    request<{ prompt: string; original: string; suggestions: string[] }>(
      '/api/v1/create/enhance-prompt',
      {
        method: 'POST',
        body: JSON.stringify({ prompt }),
      },
    ),

  publishAsset: (assetId: string, body?: { title?: string; description?: string }) =>
    request<{ asset: LibraryItem }>(`/api/v1/assets/${assetId}/publish`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  getChannel: (username: string) =>
    request<{
      profile: ProfileSummary & { bio?: string | null };
      channel: { id: string; slug: string; name: string; description?: string | null };
      assets: FeedItem[];
      authenticated?: boolean;
    }>(`/api/v1/channels/${encodeURIComponent(username)}`),

  getAiConnections: () =>
    request<{ connections?: AiConnection[]; items?: AiConnection[] }>('/api/v1/ai/connections'),

  createAiConnection: (body: {
    name: string;
    credential: string;
    paysForUsage?: boolean;
  }) =>
    request<{ connection: AiConnection }>('/api/v1/ai/connections', {
      method: 'POST',
      body: JSON.stringify({ ...body, modelId: 'gpt-6-astra' }),
    }),

  startKeyra: async (returnTo?: string) => {
    const data = await request<{
      authorizationUrl: string;
      state?: string;
      devMode?: boolean;
    }>('/api/v1/auth/keyra/start', {
      method: 'POST',
      body: JSON.stringify({
        redirectUri: `${window.location.origin}/auth/keyra/callback`,
        returnTo,
      }),
    });
    return data;
  },

  completeKeyra: (payload: { code: string; state: string }) =>
    request<{ ok: boolean; profileId: string }>('/api/v1/auth/keyra/complete', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  logout: () => request<{ ok: boolean }>('/api/v1/auth/logout', { method: 'POST' }),

  search: async (q: string) => {
    const data = await request<{ items?: FeedItem[]; results?: FeedItem[] }>(
      `/api/v1/search?q=${encodeURIComponent(q)}`,
    );
    return { items: data.items ?? data.results ?? [] };
  },

  initUpload: (body: { filename: string; contentType: string; byteSize: number; sha256?: string }) =>
    request<{ uploadId: string; storageKey: string }>('/api/v1/uploads/init', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  uploadBytes: async (uploadId: string, file: File) => {
    const res = await fetch(`/api/v1/uploads/${uploadId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body.message ?? 'Upload failed', body.error);
    }
    return res.json();
  },

  completeUpload: (uploadId: string) =>
    request<{ assetId: string; status?: string }>(`/api/v1/uploads/${uploadId}/complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  listVersions: (assetId: string) =>
    request<{ versions: Array<{ id: string; versionNumber: number; label?: string | null; createdAt: string; metadata?: Record<string, unknown> }> }>(
      `/api/v1/assets/${assetId}/versions`,
    ),

  refineAsset: (
    assetId: string,
    body: {
      kind: 'material' | 'geometry' | 'prompt';
      instructions: string;
      lockedComponentIds?: string[];
      parentVersionId?: string;
    },
  ) =>
    request<{ version?: { id: string; versionNumber: number }; jobId?: string }>(
      `/api/v1/assets/${assetId}/refine`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  restoreVersion: (assetId: string, versionId: string) =>
    request(`/api/v1/assets/${assetId}/versions/${versionId}/restore`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  duplicateAsset: (assetId: string) =>
    request<{ assetId: string }>(`/api/v1/assets/${assetId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  unpublishAsset: (assetId: string) =>
    request(`/api/v1/assets/${assetId}/unpublish`, { method: 'POST', body: JSON.stringify({}) }),

  checkout: (listingId: string) =>
    request<{ orderId: string }>('/api/v1/checkout', {
      method: 'POST',
      body: JSON.stringify({ listingId }),
    }),

  devConfirmPayment: (orderId: string, paymentEventId: string) =>
    request<{ entitlementId?: string }>('/api/v1/payments/dev-confirm', {
      method: 'POST',
      body: JSON.stringify({ orderId, paymentEventId }),
    }),

  trackEvent: (type: string, payload: Record<string, unknown> = {}) =>
    request('/api/v1/events', {
      method: 'POST',
      body: JSON.stringify({ type, payload }),
    }).catch(() => undefined),

  createProject: async (body: { title: string }) => {
    const data = await request<{ project: { id: string; currentRevisionNumber?: number } }>(
      '/api/v1/projects',
      { method: 'POST', body: JSON.stringify(body) },
    );
    return { id: data.project.id, currentRevisionNumber: data.project.currentRevisionNumber ?? 0 };
  },

  saveProjectRevision: async (
    projectId: string,
    body: { expectedRevision: number; snapshot: Record<string, unknown> },
  ) => {
    const data = await request<{
      revisionNumber?: number;
      project?: { currentRevisionNumber?: number };
    }>(`/api/v1/projects/${projectId}/revisions`, {
      method: 'PUT',
      headers: { 'If-Match': String(body.expectedRevision) },
      body: JSON.stringify({ snapshot: body.snapshot }),
    });
    return {
      revisionNumber: data.revisionNumber ?? data.project?.currentRevisionNumber ?? body.expectedRevision + 1,
    };
  },
};

export function formatPrice(cents?: number | null): string | null {
  if (cents == null || cents <= 0) return null;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(
    cents / 100,
  );
}

export function runtimeUrlFor(assetId: string) {
  return `/api/v1/assets/${assetId}/runtime`;
}
